import { Experiment, type ExperimentClient, type Variant } from "@amplitude/experiment-js-client";
import { cp } from "../config";

const FLAGS_TIMEOUT_MS = 3000;

// Resolved once the flag configs are loaded; undefined until first needed, or after a failed start.
let clientReady: Promise<ExperimentClient | undefined> | undefined;

const startClient = (): Promise<ExperimentClient | undefined> => {
    clientReady ??= (async () => {
        const deploymentKey = cp.cfg.REACT_APP_AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY;
        if (!deploymentKey) {
            return undefined;
        }

        // Local evaluation: start() only downloads the flag configs, without any user data. Exposures
        // are logged by the caller through useLogger, so they share the consent check and the identity
        // of every other event.
        const client = Experiment.initialize(deploymentKey, {
            serverZone: "eu",
            fetchOnStart: false,
            pollOnStart: false,
            automaticExposureTracking: false,
            fetchTimeoutMillis: FLAGS_TIMEOUT_MS,
        });
        // Never rejects: on a timeout or error the configs cached by an earlier visit are used, if any.
        await client.start();
        return client;
    })().catch((error) => {
        clientReady = undefined;
        console.error("Error starting Amplitude Experiment:", error);
        return undefined;
    });

    return clientReady;
};

/** The flag's variant for this bucketing id; undefined when Experiment isn't configured or reachable. */
export async function evaluateFlag(flagKey: string, bucketingId: string): Promise<Variant | undefined> {
    const client = await startClient();
    if (!client) {
        return undefined;
    }

    client.setUser({ user_id: bucketingId });
    return client.variant(flagKey);
}
